import { supabase } from './supabase';
import { ColorPreset, TemplatePreset } from '@/components/brand/color-presets/types';

export interface DbColorPreset {
  id: string;
  workspace_id: string;
  name: string;
  category: string;
  colors: any;
  is_template: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DbTemplatePreset {
  id: string;
  name: string;
  description: string;
  category: string;
  colors: any;
  logo_svg: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// In-memory cache for color presets and templates
const templatesCache = new Map<string, TemplatePreset[]>();
const workspacePresetsCache = new Map<string, ColorPreset[]>();
const prefetchPromises = new Map<string, Promise<any>>();

export const colorPresetService = {
  // Clear cache if needed (e.g., on logout or workspace switch)
  clearCache() {
    templatesCache.clear();
    workspacePresetsCache.clear();
    prefetchPromises.clear();
  },

  async getPresetsByWorkspace(workspaceId: string): Promise<ColorPreset[]> {
    // Check cache first
    if (workspacePresetsCache.has(workspaceId)) {
      return workspacePresetsCache.get(workspaceId)!;
    }

    const { data, error } = await supabase
      .from('color_presets')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const presets = (data || []).map(preset => ({
      id: preset.id,
      name: preset.name,
      colors: preset.colors,
      createdAt: preset.created_at,
      updatedAt: preset.updated_at,
    }));

    // Update cache
    workspacePresetsCache.set(workspaceId, presets);
    return presets;
  },

  async getPresetById(id: string): Promise<ColorPreset | null> {
    const { data, error } = await supabase
      .from('color_presets')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return {
      id: data.id,
      name: data.name,
      colors: data.colors,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async prefetchWorkspacePresets(workspaceId: string) {
    if (workspacePresetsCache.has(workspaceId)) return;
    if (prefetchPromises.has(`workspace-${workspaceId}`)) return prefetchPromises.get(`workspace-${workspaceId}`);

    const promise = this.getPresetsByWorkspace(workspaceId);
    prefetchPromises.set(`workspace-${workspaceId}`, promise);
    return promise;
  },

  async createPreset(
    workspaceId: string,
    preset: Omit<ColorPreset, 'id' | 'createdAt' | 'updatedAt'>,
    userId: string
  ): Promise<ColorPreset> {
    const { data, error } = await supabase
      .from('color_presets')
      .insert({
        workspace_id: workspaceId,
        name: preset.name,
        category: 'Custom',
        colors: preset.colors,
        is_template: false,
        created_by: userId,
      })
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    workspacePresetsCache.delete(workspaceId);

    return {
      id: data.id,
      name: data.name,
      colors: data.colors,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async updatePreset(
    presetId: string,
    updates: Partial<Omit<ColorPreset, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<ColorPreset> {
    const dbUpdates: any = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.colors !== undefined) dbUpdates.colors = updates.colors;

    const { data, error } = await supabase
      .from('color_presets')
      .update(dbUpdates)
      .eq('id', presetId)
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    workspacePresetsCache.delete(data.workspace_id);

    return {
      id: data.id,
      name: data.name,
      colors: data.colors,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async deletePreset(presetId: string): Promise<void> {
    const { error } = await supabase
      .from('color_presets')
      .delete()
      .eq('id', presetId);

    if (error) throw error;

    // Invalidate cache
    workspacePresetsCache.clear();
  },

  async duplicatePreset(
    presetId: string,
    workspaceId: string,
    userId: string
  ): Promise<ColorPreset> {
    const { data: original, error: fetchError } = await supabase
      .from('color_presets')
      .select('*')
      .eq('id', presetId)
      .single();

    if (fetchError) throw fetchError;

    const { data, error } = await supabase
      .from('color_presets')
      .insert({
        workspace_id: workspaceId,
        name: `${original.name} (Copy)`,
        category: original.category,
        colors: original.colors,
        is_template: false,
        created_by: userId,
      })
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    workspacePresetsCache.delete(workspaceId);

    return {
      id: data.id,
      name: data.name,
      colors: data.colors,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async getBookmarkedTemplates(
    workspaceId: string,
    userId: string
  ): Promise<string[]> {
    const { data, error } = await supabase
      .from('color_preset_bookmarks')
      .select('template_id')
      .eq('workspace_id', workspaceId)
      .eq('user_id', userId);

    if (error) throw error;

    return (data || []).map(bookmark => bookmark.template_id);
  },

  async addBookmark(
    workspaceId: string,
    userId: string,
    templateId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('color_preset_bookmarks')
      .insert({
        workspace_id: workspaceId,
        user_id: userId,
        template_id: templateId,
      });

    if (error) throw error;
  },

  async removeBookmark(
    workspaceId: string,
    userId: string,
    templateId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('color_preset_bookmarks')
      .delete()
      .eq('workspace_id', workspaceId)
      .eq('user_id', userId)
      .eq('template_id', templateId);

    if (error) throw error;
  },

  async toggleBookmark(
    workspaceId: string,
    userId: string,
    templateId: string
  ): Promise<boolean> {
    const { data: existing } = await supabase
      .from('color_preset_bookmarks')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('user_id', userId)
      .eq('template_id', templateId)
      .maybeSingle();

    if (existing) {
      await this.removeBookmark(workspaceId, userId, templateId);
      return false;
    } else {
      await this.addBookmark(workspaceId, userId, templateId);
      return true;
    }
  },

  async prefetchTemplates() {
    if (templatesCache.has('all')) return;
    if (prefetchPromises.has('templates')) return prefetchPromises.get('templates');

    const promise = this.getAllTemplates();
    prefetchPromises.set('templates', promise);
    return promise;
  },

  async getAllTemplates(): Promise<TemplatePreset[]> {
    // Check cache first
    if (templatesCache.has('all')) {
      return templatesCache.get('all')!;
    }

    const { data, error } = await supabase
      .from('color_preset_templates')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });

    if (error) throw error;

    const templates = (data || []).map(template => ({
      id: template.id,
      name: template.name,
      description: template.description,
      category: template.category,
      colors: template.colors,
      logoSvg: template.logo_svg || undefined,
    }));

    // Update cache
    templatesCache.set('all', templates);
    return templates;
  },

  async createTemplate(template: Omit<TemplatePreset, 'id'>): Promise<string> {
    const { data, error } = await supabase
      .from('color_preset_templates')
      .insert({
        name: template.name,
        description: template.description,
        category: template.category,
        colors: template.colors,
        logo_svg: template.logoSvg || null,
        is_active: true,
        sort_order: 0,
      })
      .select('id')
      .single();

    if (error) throw error;

    return data.id;
  },

  async seedTemplates(templates: TemplatePreset[]): Promise<{ success: number; failed: number; errors: Array<{ template: string; error: any }> }> {
    const batchSize = 50;
    let successCount = 0;
    let failedCount = 0;
    const errors: Array<{ template: string; error: any }> = [];

    for (let i = 0; i < templates.length; i += batchSize) {
      const batch = templates.slice(i, i + batchSize);
      const records = batch.map(template => ({
        id: template.id,
        name: template.name,
        description: template.description,
        category: template.category,
        colors: template.colors,
        logo_svg: template.logoSvg || null,
        is_active: true,
        sort_order: 0,
      }));

      const { error } = await supabase
        .from('color_preset_templates')
        .upsert(records, {
          onConflict: 'id',
        });

      if (error) {
        console.error(`Failed to seed batch ${i / batchSize + 1}:`, error);
        failedCount += batch.length;
        errors.push({ template: `Batch ${i / batchSize + 1}`, error });
      } else {
        successCount += batch.length;
        console.log(`Successfully seeded batch ${i / batchSize + 1} (${batch.length} templates)`);
      }
    }

    console.log(`Seeding complete: ${successCount} succeeded, ${failedCount} failed`);
    return { success: successCount, failed: failedCount, errors };
  },
};
